import React, { useMemo, useState } from "react";
import { Box, Button, InputAdornment, Paper, SvgIcon, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import * as d3 from "d3";
import { useNavigate } from "react-router-dom";
import LayoutMapping, { useLayoutMappingMetrics } from "../components/LayoutMapping";
import WorkspaceSidebar from "../components/WorkspaceSidebar";
import { ARAGON_COLORS, ARAGON_FONTS } from "../theme/AragonTheme";
import { BUYER_CASES, formatVndCompact } from "./buyerWorkspaceData";
import { getSellerMarketStatus } from "./advisorWorkspaceData";

function SearchIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="m16 16 4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </SvgIcon>
  );
}

function ArrowIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M5 12h13m-5-5 5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </SvgIcon>
  );
}

function useAdvisorDealRoomMetrics() {
  const shared = useLayoutMappingMetrics();
  return useMemo(() => {
    const width = shared.viewport.width;
    const height = shared.viewport.height;
    return {
      ...shared,
      contentWidth: Math.round(d3.scaleLinear().domain([1024, 1920]).range([760, 1100]).clamp(true)(width)),
      contentTop: Math.round(d3.scaleLinear().domain([620, 1080]).range([20, 30]).clamp(true)(height)),
      rowHeight: Math.round(d3.scaleLinear().domain([768, 1920]).range([96, 112]).clamp(true)(width)),
      searchHeight: Math.round(d3.scaleLinear().domain([768, 1920]).range([38, 42]).clamp(true)(width)),
    };
  }, [shared]);
}

function statusLabel(status) {
  if (status === "approved") return "Market ready";
  if (status === "held") return "Held";
  return "Pending review";
}

export default function AdvisorDealRoom() {
  const metrics = useAdvisorDealRoomMetrics();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const sellers = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    return BUYER_CASES
      .map((seller) => ({ ...seller, marketStatus: getSellerMarketStatus(seller.id) }))
      .filter((seller) => !normalized || `${seller.company} ${seller.sector} ${seller.subsector} ${seller.id}`.toLocaleLowerCase().includes(normalized))
      .sort((a, b) => {
        const order = { pending: 0, held: 1, approved: 2 };
        return (order[a.marketStatus] ?? 3) - (order[b.marketStatus] ?? 3) || new Date(b.updatedAt) - new Date(a.updatedAt);
      });
  }, [query]);
  return (
    <LayoutMapping metrics={metrics} sidebar={<WorkspaceSidebar metrics={metrics} workspace="advisor" />}>
      <Box>
        <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.eyebrowSize}px`, fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", color: ARAGON_COLORS.burgundy }}>Advisor workspace</Typography>
        <Typography component="h1" sx={{ mt: "5px", fontFamily: ARAGON_FONTS.primary, fontSize: `${metrics.titleSize}px`, lineHeight: 1.04, color: ARAGON_COLORS.navy }}>Deal Room</Typography>
        <Typography sx={{ mt: "6px", maxWidth: 760, fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.bodySize}px`, color: ARAGON_COLORS.steel }}>Review valuation-complete sellers and control which opportunities are ready to enter the market.</Typography>
        <TextField
          fullWidth
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search sellers..."
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: ARAGON_COLORS.steel }} /></InputAdornment> }}
          inputProps={{ style: { fontFamily: ARAGON_FONTS.secondary, fontSize: Math.max(10, metrics.bodySize - 1) } }}
          sx={{ mt: `${metrics.cardGap + 14}px`, "& .MuiOutlinedInput-root": { height: `${metrics.searchHeight}px`, borderRadius: `${metrics.cardRadius}px`, bgcolor: ARAGON_COLORS.sellerSidebar, "& fieldset": { borderColor: alpha(ARAGON_COLORS.navy, 0.10) }, "&:hover fieldset": { borderColor: alpha(ARAGON_COLORS.navy, 0.18) }, "&.Mui-focused fieldset": { borderColor: ARAGON_COLORS.navy, borderWidth: "1px" } } }}
        />
        <Paper elevation={0} sx={{ mt: `${metrics.cardGap}px`, border: `1px solid ${alpha(ARAGON_COLORS.navy, 0.10)}`, borderRadius: `${metrics.cardRadius}px`, bgcolor: ARAGON_COLORS.sellerSidebar, overflow: "hidden" }}>
          {sellers.map((seller, index) => (
            <Box key={seller.id} sx={{ minHeight: `${metrics.rowHeight}px`, px: `${metrics.cardPadding}px`, py: "14px", display: "grid", gridTemplateColumns: metrics.compact ? "1fr" : "minmax(0,1.55fr) repeat(3, minmax(110px,.65fr)) auto", gap: "14px", alignItems: "center", borderTop: index === 0 ? "none" : `1px solid ${alpha(ARAGON_COLORS.navy, 0.07)}` }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(8, metrics.eyebrowSize - 1)}px`, letterSpacing: "0.10em", color: ARAGON_COLORS.steel }}>{seller.id}</Typography>
                <Typography sx={{ mt: "3px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(10, metrics.bodySize - 1)}px`, fontWeight: 700, color: ARAGON_COLORS.navy }}>{seller.company}</Typography>
                <Typography sx={{ mt: "3px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(8, metrics.bodySize - 3)}px`, color: ARAGON_COLORS.steel, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{seller.sector} · {seller.location}</Typography>
              </Box>
              {[["Valuation", formatVndCompact(seller.transactionValue)], ["Documents", `${seller.documents.length} files`], ["Market status", statusLabel(seller.marketStatus)]].map(([label, value]) => (
                <Box key={label}>
                  <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(7, metrics.eyebrowSize - 1)}px`, letterSpacing: "0.10em", textTransform: "uppercase", color: ARAGON_COLORS.steel }}>{label}</Typography>
                  <Typography sx={{ mt: "5px", fontFamily: label === "Valuation" ? ARAGON_FONTS.primary : ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px`, fontWeight: label === "Valuation" ? 400 : 650, color: ARAGON_COLORS.navy }}>{value}</Typography>
                </Box>
              ))}
              <Button onClick={() => navigate(`/advisor/deal-room/${seller.id}`)} endIcon={<ArrowIcon sx={{ fontSize: "15px !important" }} />} sx={{ height: "34px", px: "14px", bgcolor: ARAGON_COLORS.navy, color: ARAGON_COLORS.sellerSidebar, borderRadius: "7px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px`, "&:hover": { bgcolor: alpha(ARAGON_COLORS.navy, 0.90) } }}>{seller.marketStatus === "pending" ? "Review" : "Open"}</Button>
            </Box>
          ))}
          {sellers.length === 0 && <Box sx={{ p: `${metrics.cardPadding + 8}px` }}><Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.bodySize}px`, color: ARAGON_COLORS.steel }}>No sellers match this search.</Typography></Box>}
        </Paper>
      </Box>
    </LayoutMapping>
  );
}
